import { Injectable } from '@angular/core';
import { CaracteristiqueTypeEnum, InfoAdditionnelleEnum } from '@enums';
import {
  Composant,
  ComposantEntityState,
  DynamicType,
  InfosToMutateOfflineDbWithNewComposant,
  Patrimoine,
  SocieteCaracteristique,
  SocieteComposant,
  SocieteComposantFamille,
  Valeur,
  ValeurEntityState,
  ValeurFichier
} from '@get/api-interfaces';
import {
  ComposantAttenduDocument,
  ComposantDocument,
  MyDatabaseCollections,
  PatrimoineDocument
} from '@get/interfaces';
import { ComposantActions, ComposantGeneratedActions, EspaceGeneratedActions } from '@get/store/actions';
import { ComposantApiService } from '@get/store/api-services';
import { getMultiAction } from '@get/store/configs/batched-actions';
import { AppState } from '@get/store/configs/reducers';
import { getDefaultDeleteComposantActions, getDefaultDeleteValeurActions } from '@get/store/effects';
import { ComposantSelectors, ValeurSelectors } from '@get/store/selectors';
import { filterOnlyUsefulComposants } from '@get/utils';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Action, Store } from '@ngrx/store';
import { areValeursEqual, compareNumberCustomWithNegatives, transformArrayToObject } from '@utils';
import fastDeepEqual from 'fast-deep-equal';
import { RxDocument } from 'rxdb';
import {
  ReplaySubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  first,
  map,
  of,
  startWith,
  switchMap,
  tap
} from 'rxjs';
import { v4 as uuidGenerator } from 'uuid';
import { ComposantAttenduDbService } from './composant-attendu.db.service';
import { DbService } from './db.service';

@Injectable({ providedIn: 'root' })
export class ComposantDbService {
  constructor(
    private dbService: DbService,
    private store$: Store<AppState>,
    private composantApiService: ComposantApiService,
    private readonly composantAttenduDbService: ComposantAttenduDbService
  ) {}

  // ====================================================== //
  // ====================== CREATION ====================== //
  // ====================================================== //
  public async createComposant(
    params: {
      idSocieteComposant: number;
      idEspace: number;
      patrimoine: Patrimoine;
      societeComposant: SocieteComposant;
      idUser?: number;
    },
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void
  ): Promise<string> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const { newIdComposant, newIdValeur } = await this.getLowestIdComposantAndIdValeur(databaseCollections);
    const generatedUUID = uuidGenerator();

    const newDocument = await databaseCollections.composants.insert({
      idComposant: newIdComposant,
      uuid: generatedUUID,
      idEspace: params.idEspace,
      espace: { idEspace: params.idEspace },
      idSocieteComposant: params.idSocieteComposant,
      societeComposant: params.idSocieteComposant,
      idUser: params.idUser,
      user: params.idUser,
      updatedAt: new Date().toISOString(),
      isSynced: false,
      valeurs:
        params.societeComposant?.societeCaracteristiques?.map((el, idx) => ({
          idValeur: newIdValeur - idx,
          idSocieteCaracteristique: el.idSocieteCaracteristique,
          societeCaracteristique: el.idSocieteCaracteristique
        })) || []
    });
    const updatedComposantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
      params.patrimoine.idPatrimoine,
      params.idSocieteComposant,
      newDocument,
      params.idUser
    );
    this.synchronizeIdComposantFromIndexedDb(generatedUUID, callback, updatedComposantAttendus);
    return generatedUUID;
  }

  public async massComposantCreation(
    societeComposant: SocieteComposant,
    toCreate: Composant[],
    idUser?: number,
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void
  ): Promise<void> {
    const nbCaracteristiques = societeComposant.societeCaracteristiques?.length;
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const { newIdComposant, newIdValeur } = await this.getLowestIdComposantAndIdValeur(databaseCollections);
    const composantsWithValeursObj = toCreate.map(el => ({
      ...el,
      valeursObj: transformArrayToObject(el.valeurs, { key: 'idSocieteCaracteristique' }) as DynamicType<Valeur>
    }));
    const formattedComposants = composantsWithValeursObj.map((composant, composantIdx) => ({
      idEspace: composant.idEspace,
      idSocieteComposant: composant.idSocieteComposant,
      idComposant: newIdComposant - composantIdx,
      uuid: uuidGenerator(),
      espace: { idEspace: composant.idEspace },
      societeComposant: composant.idSocieteComposant,
      idUser: idUser,
      user: idUser,
      updatedAt: new Date().toISOString(),
      isSynced: false,
      valeursObj: undefined,
      valeurs:
        societeComposant.societeCaracteristiques?.map((societeCaracteristique, caracIdx) =>
          !composant.valeursObj[societeCaracteristique.idSocieteCaracteristique]
            ? {
                idValeur: newIdValeur - caracIdx - nbCaracteristiques * composantIdx,
                idSocieteCaracteristique: societeCaracteristique.idSocieteCaracteristique,
                societeCaracteristique: societeCaracteristique.idSocieteCaracteristique
              }
            : {
                ...composant.valeursObj[societeCaracteristique.idSocieteCaracteristique],
                societeCaracteristique: societeCaracteristique.idSocieteCaracteristique,
                idValeur: newIdValeur - caracIdx - nbCaracteristiques * composantIdx,
                societeCaracteristiqueChoix:
                  composant.valeursObj[societeCaracteristique.idSocieteCaracteristique].idSocieteCaracteristiqueChoix
              }
        ) || []
    }));
    await databaseCollections.composants.bulkInsert(formattedComposants);
    const patrimoinesInvolved: number[] = [];
    await Promise.all(
      formattedComposants.map(async composant => {
        const patrimoineDocument = await this.findPatrimoineDocumentForEspace({
          idEspace: composant.idEspace,
          databaseCollections
        });
        if (patrimoineDocument) {
          patrimoinesInvolved.push(+patrimoineDocument.idPatrimoine);
        }
        return;
      })
    );

    const patrimoinesDocuments = await databaseCollections.patrimoines.find().exec();
    const patrimoines = patrimoinesDocuments
      .map(el => el.getLatest().toJSON() as unknown as Patrimoine)
      .map(el => ({ ...el, idPatrimoine: +el.idPatrimoine }));
    const patrimoinesObj = transformArrayToObject(patrimoines, { key: 'idPatrimoine' });

    const highestPatrimoines: number[] = [];
    for (let i = 0; i < patrimoinesInvolved?.length; i++) {
      const highestPatrimoine = this.composantAttenduDbService.findHighestAncestorId(
        patrimoinesObj,
        patrimoinesInvolved[i]
      );
      if (highestPatrimoine && !highestPatrimoines.includes(highestPatrimoine)) {
        highestPatrimoines.push(highestPatrimoine);
      }
    }

    const updatedComposantAttendus = [];
    for (let i = 0; i < highestPatrimoines?.length; i++) {
      const composantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
        highestPatrimoines[i],
        societeComposant.idSocieteComposant,
        undefined,
        idUser
      );
      updatedComposantAttendus.push(...composantAttendus);
    }
    this.synchronizeFromIndexedDb(callback, updatedComposantAttendus);
  }

  public async duplicateComposant(
    params: {
      composant: Composant;
      idEspace: number;
      patrimoine: Patrimoine;
      idUser?: number;
    },
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void
  ): Promise<string> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const { newIdComposant, newIdValeur } = await this.getLowestIdComposantAndIdValeur(databaseCollections);
    const generatedUUID = uuidGenerator();

    const newDocument = await databaseCollections.composants.insert({
      idComposant: newIdComposant,
      uuid: generatedUUID,
      idEspace: params.idEspace,
      espace: { idEspace: params.idEspace },
      idSocieteComposant: params.composant.idSocieteComposant,
      societeComposant: params.composant.idSocieteComposant,
      idUser: params.idUser,
      user: params.idUser,
      updatedAt: new Date().toISOString(),
      isSynced: false,
      valeurs:
        params.composant?.valeurs?.map((el, idx) => ({
          ...el,
          idValeur: newIdValeur - idx,
          societeCaracteristique: el.idSocieteCaracteristique,
          societeCaracteristiqueChoix: el.idSocieteCaracteristiqueChoix
        })) || []
    });
    const updatedComposantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
      params.patrimoine.idPatrimoine,
      params.composant.idSocieteComposant,
      newDocument,
      params.idUser
    );
    this.synchronizeIdComposantFromIndexedDb(generatedUUID, callback, updatedComposantAttendus);
    return generatedUUID;
  }

  public async massComposantDuplication(
    toCreate: Composant[],
    societeCaracteristiques: SocieteCaracteristique[],
    idsSocieteComposant: number[],
    idUser?: number,
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void
  ): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const { newIdComposant, newIdValeur } = await this.getLowestIdComposantAndIdValeur(databaseCollections);
    const composantsWithValeursObj = toCreate.map(el => ({
      ...el,
      valeursObj: transformArrayToObject(el.valeurs, { key: 'idSocieteCaracteristique' }) as DynamicType<Valeur>
    }));
    const formattedComposants = composantsWithValeursObj.map((composant, composantIdx) => {
      const societeCaracteristiquesFiltered = societeCaracteristiques.filter(
        el => el.idSocieteComposant === composant.idSocieteComposant
      );
      return {
        idEspace: composant.idEspace,
        idSocieteComposant: composant.idSocieteComposant,
        idComposant: newIdComposant - composantIdx,
        uuid: uuidGenerator(),
        espace: { idEspace: composant.idEspace },
        societeComposant: composant.idSocieteComposant,
        idUser: idUser,
        user: idUser,
        updatedAt: new Date().toISOString(),
        isSynced: false,
        valeursObj: undefined,
        valeurs:
          societeCaracteristiquesFiltered?.map((societeCaracteristique, caracIdx) =>
            !composant.valeursObj[societeCaracteristique.idSocieteCaracteristique]
              ? {
                  idValeur: newIdValeur - caracIdx - societeCaracteristiquesFiltered?.length * composantIdx,
                  idSocieteCaracteristique: societeCaracteristique.idSocieteCaracteristique,
                  societeCaracteristique: societeCaracteristique.idSocieteCaracteristique
                }
              : {
                  ...composant.valeursObj[societeCaracteristique.idSocieteCaracteristique],
                  societeCaracteristique: societeCaracteristique.idSocieteCaracteristique,
                  idValeur: newIdValeur - caracIdx - societeCaracteristiquesFiltered?.length * composantIdx,
                  societeCaracteristiqueChoix:
                    composant.valeursObj[societeCaracteristique.idSocieteCaracteristique].idSocieteCaracteristiqueChoix
                }
          ) || []
      };
    });

    await databaseCollections.composants.bulkInsert(formattedComposants);
    const patrimoinesInvolved: number[] = [];
    await Promise.all(
      formattedComposants.map(async composant => {
        const patrimoineDocument = await this.findPatrimoineDocumentForEspace({
          idEspace: composant.idEspace,
          databaseCollections
        });
        if (patrimoineDocument) {
          patrimoinesInvolved.push(+patrimoineDocument.idPatrimoine);
        }
        return;
      })
    );

    const patrimoinesDocuments = await databaseCollections.patrimoines.find().exec();
    const patrimoines = patrimoinesDocuments
      .map(el => el.getLatest().toJSON() as unknown as Patrimoine)
      .map(el => ({ ...el, idPatrimoine: +el.idPatrimoine }));
    const patrimoinesObj = transformArrayToObject(patrimoines, { key: 'idPatrimoine' });

    const highestPatrimoines: number[] = [];
    for (let i = 0; i < patrimoinesInvolved?.length; i++) {
      const highestPatrimoine = this.composantAttenduDbService.findHighestAncestorId(
        patrimoinesObj,
        patrimoinesInvolved[i]
      );
      if (highestPatrimoine && !highestPatrimoines.includes(highestPatrimoine)) {
        highestPatrimoines.push(highestPatrimoine);
      }
    }
    const updatedComposantAttendus = [];
    for (let i = 0; i < highestPatrimoines?.length; i++) {
      for (let j = 0; j < idsSocieteComposant?.length; j++) {
        const composantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
          highestPatrimoines[i],
          idsSocieteComposant[j],
          undefined,
          idUser
        );
        updatedComposantAttendus.push(...composantAttendus);
      }
    }
    this.synchronizeFromIndexedDb(callback, updatedComposantAttendus);
  }

  // ====================================================== //
  // ==================== MODIFICATION ==================== //
  // ====================================================== //
  public async updateComposantValeur(params: {
    valeur: Valeur;
    composant: Composant;
    patrimoine: Patrimoine;
    idUser?: number;
  }): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections['composants']
      .findOne({ selector: { uuid: params.composant.uuid } })
      .exec();
    if (composantDocument) {
      const composantToUpdate = composantDocument.getLatest().toJSON();
      const oldValeur = params.composant?.valeurs?.find(valeur => valeur.idValeur === params.valeur.idValeur);
      const lowestIdValeurFichier = await this.getLowestIdValeurFichier(databaseCollections);
      await composantDocument.incrementalPatch({
        idUser: params.idUser,
        user: params.idUser,
        updatedAt: new Date().toISOString(),
        isSynced: false,
        valeurs: composantToUpdate.valeurs?.map(valeur =>
          valeur.idValeur !== params.valeur.idValeur
            ? valeur
            : ({
                ...valeur,
                ...params.valeur,
                isSynced: false,
                valeurFichiers: !params.valeur.valeurFichiers
                  ? params.valeur.valeurFichiers
                  : valeur.valeurFichiers?.concat(
                      params.valeur.valeurFichiers?.map((valeurFichier, idx) => ({
                        ...valeurFichier,
                        idValeurFichier: valeurFichier.idValeurFichier ?? lowestIdValeurFichier.newIdValeurFichier - idx
                      })) || []
                    )
              } as any)
        )
      });
      let updatedComposantAttendus: ComposantAttenduDocument[] = [];
      if (
        oldValeur?.societeCaracteristique?.type === CaracteristiqueTypeEnum.integer &&
        oldValeur?.societeCaracteristique?.infoAdditionnelle === InfoAdditionnelleEnum.decompte
      ) {
        updatedComposantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
          params.patrimoine.idPatrimoine,
          params.composant.societeComposant?.idSocieteComposant,
          undefined,
          params.idUser
        );
      }
      this.synchronizeIdComposantFromIndexedDb(params.composant?.uuid, undefined, updatedComposantAttendus);
    }
  }

  public async massComposantModification(toModify: Valeur[], idUser?: number): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const valeursObj: DynamicType<Valeur> = transformArrayToObject(toModify, { key: 'idValeur' });
    const composantsDocuments = await databaseCollections.composants.find().exec();
    const composants = composantsDocuments.map(el => el.getLatest().toMutableJSON());
    const filteredComposants = composants.filter(composant =>
      composant.valeurs?.some(valeur => valeursObj[valeur.idValeur])
    );
    const formattedComposants = filteredComposants.map(composant => ({
      ...composant,
      idUser: idUser,
      user: idUser,
      updatedAt: new Date().toISOString(),
      isSynced: false,
      valeurs: composant.valeurs?.map(valeur =>
        !valeursObj[valeur.idValeur]
          ? valeur
          : {
              ...valeursObj[valeur.idValeur],
              societeCaracteristique: valeur.idSocieteCaracteristique,
              societeCaracteristiqueChoix: valeursObj[valeur.idValeur].idSocieteCaracteristiqueChoix
            }
      )
    }));
    await databaseCollections.composants.bulkUpsert(formattedComposants);

    const patrimoinesInvolved: number[] = [];

    const societeComposants: number[] = [];
    await Promise.all(
      formattedComposants.map(async composant => {
        const patrimoineDocument = await this.findPatrimoineDocumentForEspace({
          idEspace: composant.idEspace,
          databaseCollections
        });
        if (patrimoineDocument) {
          patrimoinesInvolved.push(+patrimoineDocument.idPatrimoine);
        }
        if (!societeComposants?.includes(composant.idSocieteComposant)) {
          societeComposants.push(composant.idSocieteComposant);
        }
        return;
      })
    );

    const patrimoinesDocuments = await databaseCollections.patrimoines.find().exec();
    const patrimoines = patrimoinesDocuments
      .map(el => el.getLatest().toJSON() as unknown as Patrimoine)
      .map(el => ({ ...el, idPatrimoine: +el.idPatrimoine }));
    const patrimoinesObj = transformArrayToObject(patrimoines, { key: 'idPatrimoine' });

    const highestPatrimoines: number[] = [];
    for (let i = 0; i < patrimoinesInvolved?.length; i++) {
      const highestPatrimoine = this.composantAttenduDbService.findHighestAncestorId(
        patrimoinesObj,
        patrimoinesInvolved[i]
      );
      if (highestPatrimoine && !highestPatrimoines.includes(highestPatrimoine)) {
        highestPatrimoines.push(highestPatrimoine);
      }
    }

    const updatedComposantAttendus = [];
    for (let i = 0; i < highestPatrimoines?.length; i++) {
      for (let j = 0; j < societeComposants?.length; j++) {
        const composantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
          highestPatrimoines[i],
          societeComposants[j],
          undefined,
          idUser
        );
        updatedComposantAttendus.push(...composantAttendus);
      }
    }

    this.synchronizeFromIndexedDb(undefined, updatedComposantAttendus);
  }

  public async updateComposantEspace(params: {
    idComposant: number;
    newIdEspace: number;
    oldIdEspace: number;
    uuid: string;
    idUser?: number;
  }): Promise<void> {
    if (!params.idComposant || !params.newIdEspace || !params.oldIdEspace) {
      return;
    }
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections['composants']
      .findOne({ selector: { uuid: params.uuid } })
      .exec();
    if (composantDocument) {
      await composantDocument.incrementalPatch({
        isSynced: false,
        idEspace: params.newIdEspace,
        espace: { idEspace: params.newIdEspace },
        idUser: params.idUser,
        user: params.idUser
      });
    }
    const jsonComposant = composantDocument?.getLatest().toJSON();
    const idComposant = jsonComposant?.idComposant ? +jsonComposant.idComposant : params.idComposant;

    const actions: Action[] = [];
    if (params.oldIdEspace !== params.newIdEspace) {
      actions.push(
        ComposantGeneratedActions.addEspaceSuccess({
          idComposant: idComposant,
          idEspace: params.newIdEspace
        })
      );
      actions.push(
        ComposantGeneratedActions.deleteManyEspaceSuccess({
          idComposants: [idComposant],
          idEspaces: [params.oldIdEspace]
        })
      );
      actions.push(
        EspaceGeneratedActions.addManyComposantSuccess({
          idEspace: params.newIdEspace,
          idComposants: [idComposant]
        })
      );
      actions.push(
        EspaceGeneratedActions.deleteManyComposantSuccess({
          idComposants: [idComposant],
          idEspaces: [params.oldIdEspace]
        })
      );
    }
    if (actions?.length) {
      // TODO: Check si c'est vraiment nécessaire de le faire à la main car l'update du composant renormalisera les composants dans les bons espaces
      this.store$.dispatch(getMultiAction(actions, '[Composant] update composant espace'));
    }
    this.synchronizeIdComposantFromIndexedDb(params.uuid);
  }

  // ====================================================== //
  // ====================== DEPLACER ====================== //
  // ====================================================== //
  public async moveComposant(params: {
    composant: Composant;
    newIdEspace: number;
    oldIdEspace: number;
    newPatrimoine: Patrimoine;
    oldIdPatrimoine: number;
    idUser?: number;
  }): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections['composants']
      .findOne({ selector: { uuid: params.composant.uuid } })
      .exec();
    if (composantDocument) {
      await composantDocument.incrementalPatch({
        isSynced: false,
        idEspace: params.newIdEspace,
        espace: { idEspace: params.newIdEspace },
        idUser: params.idUser,
        user: params.idUser
      });
    }
    const jsonComposant = composantDocument?.getLatest().toJSON();
    const idComposant = jsonComposant?.idComposant ? +jsonComposant.idComposant : params.composant.idComposant;
    const actions: Action[] = [];
    if (params.oldIdEspace !== params.newIdEspace) {
      actions.push(
        ComposantGeneratedActions.addEspaceSuccess({
          idComposant,
          idEspace: params.newIdEspace
        })
      );
      actions.push(
        ComposantGeneratedActions.deleteManyEspaceSuccess({
          idComposants: [idComposant],
          idEspaces: [params.oldIdEspace]
        })
      );
      actions.push(
        EspaceGeneratedActions.addManyComposantSuccess({
          idEspace: params.newIdEspace,
          idComposants: [idComposant]
        })
      );
      actions.push(
        EspaceGeneratedActions.deleteManyComposantSuccess({
          idComposants: [idComposant],
          idEspaces: [params.oldIdEspace]
        })
      );
    }
    if (actions?.length) {
      this.store$.dispatch(getMultiAction(actions, '[Composant] move composant'));
    }

    const idsPatrimoine: number[] = [];
    const oldPatrimoine = await this.findPatrimoineDocumentForEspace({ idEspace: params.oldIdEspace });
    if (oldPatrimoine) {
      idsPatrimoine.push(+oldPatrimoine.idPatrimoine);
    }
    const newPatrimoine = await this.findPatrimoineDocumentForEspace({ idEspace: params.newIdEspace });
    if (newPatrimoine && newPatrimoine.idPatrimoine !== oldPatrimoine?.idPatrimoine) {
      idsPatrimoine.push(+newPatrimoine.idPatrimoine);
    }

    const patrimoinesDocuments = await databaseCollections.patrimoines.find().exec();
    const patrimoines = patrimoinesDocuments
      .map(el => el.getLatest().toJSON() as unknown as Patrimoine)
      .map(el => ({ ...el, idPatrimoine: +el.idPatrimoine }));
    const patrimoinesObj = transformArrayToObject(patrimoines, { key: 'idPatrimoine' });

    const highestPatrimoines: number[] = [];
    for (let i = 0; i < idsPatrimoine?.length; i++) {
      const highestPatrimoine = this.composantAttenduDbService.findHighestAncestorId(patrimoinesObj, idsPatrimoine[i]);
      if (highestPatrimoine && !highestPatrimoines.includes(highestPatrimoine)) {
        highestPatrimoines.push(highestPatrimoine);
      }
    }

    const updatedComposantAttendus = [];
    for (let i = 0; i < highestPatrimoines.length; i++) {
      const composantAttendus = await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
        highestPatrimoines[i],
        params.composant.societeComposant?.idSocieteComposant,
        undefined,
        params.idUser
      );
      updatedComposantAttendus.push(...composantAttendus);
    }

    this.synchronizeIdComposantFromIndexedDb(params.composant.uuid, undefined, updatedComposantAttendus);
  }

  // ====================================================== //
  // ====================== SUPPRIMER ===================== //
  // ====================================================== //
  public async deleteComposant(composant: Composant, patrimoine: Patrimoine, idUser?: number): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections['composants']
      .findOne({ selector: { uuid: composant?.uuid } })
      .exec();
    if (composantDocument) {
      const idComposant = +composantDocument.getLatest().toJSON().idComposant;
      if (idComposant < 0) {
        await databaseCollections['composants'].bulkRemove([composant?.uuid]);
      } else {
        await composantDocument.incrementalPatch({ isSynced: false, deleted: true });
      }

      const updatedComposantAttendus =
        await this.composantAttenduDbService.recalculateComposantAttenduTreeForPatrimoine(
          patrimoine?.idPatrimoine,
          composant?.societeComposant?.idSocieteComposant,
          undefined,
          idUser
        );

      if (idComposant < 0) {
        if (updatedComposantAttendus?.length) {
          await this.composantAttenduDbService.updateComposantAttendus(updatedComposantAttendus);
        }
        await this.composantAttenduDbService.synchronizeFromIndexedDb();
      } else {
        this.synchronizeIdComposantFromIndexedDb(composant?.uuid, undefined, updatedComposantAttendus);
      }

      this.removeIdComposantFromStore(idComposant);
    }
  }

  public async removeValeurFichier(params: {
    valeurFichier: ValeurFichier;
    valeur: Valeur;
    composant: Composant;
    idUser?: number;
  }): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections.composants
      .findOne({ selector: { uuid: params.composant?.uuid } })
      .exec();
    if (composantDocument) {
      const composant = composantDocument.getLatest().toMutableJSON();
      const formattedComposant = {
        ...composant,
        idUser: params.idUser,
        user: params.idUser,
        updatedAt: new Date().toISOString(),
        isSynced: false,
        valeurs: composant.valeurs?.map(valeur =>
          valeur.idValeur !== params.valeur.idValeur
            ? valeur
            : {
                ...valeur,
                valeurFichiers: valeur.valeurFichiers?.filter(
                  valeurFichier => valeurFichier.idValeurFichier !== params.valeurFichier.idValeurFichier
                )
              }
        )
      };
      await composantDocument.incrementalPatch(formattedComposant);
      this.synchronizeIdComposantFromIndexedDb(composant?.uuid);
    }
  }

  // ====================================================== //
  // ======================== UTILS ======================= //
  // ====================================================== //
  private async getLowestIdComposantAndIdValeur(
    databaseCollections?: MyDatabaseCollections
  ): Promise<{ newIdValeur: number; newIdComposant: number }> {
    if (!databaseCollections) {
      databaseCollections = await this.dbService.getDatabaseCollection();
    }
    const oldComposantsDocuments = await databaseCollections.composants.find().exec();
    const oldComposants = oldComposantsDocuments.map(el => el.getLatest().toJSON());
    const lowestIdComposant = Math.min(...oldComposants.map(el => +el.idComposant));
    const lowestIdValeur = Math.min(
      ...oldComposants
        .map(el => el.valeurs?.map(valeur => valeur.idValeur))
        .flat()
        .filter(v => !!v)
    );
    return {
      newIdComposant: lowestIdComposant < 0 ? lowestIdComposant - 1 : -1,
      newIdValeur: lowestIdValeur < 0 ? lowestIdValeur - 1 : -1
    };
  }

  private async getLowestIdValeurFichier(
    databaseCollections?: MyDatabaseCollections
  ): Promise<{ newIdValeurFichier: number }> {
    if (!databaseCollections) {
      databaseCollections = await this.dbService.getDatabaseCollection();
    }
    const oldComposantsDocuments = await databaseCollections.composants.find().exec();
    const oldComposants = oldComposantsDocuments.map(el => el.getLatest().toJSON());
    const lowestIdValeurFichier = Math.min(
      ...oldComposants
        .map(el =>
          el.valeurs?.map(valeur => valeur.valeurFichiers?.map(valeurFichier => valeurFichier.idValeurFichier) || [])
        )
        .flat(2)
        .filter(v => !!v)
    );
    return {
      newIdValeurFichier: lowestIdValeurFichier < 0 ? lowestIdValeurFichier - 1 : -1
    };
  }

  private async findPatrimoineDocumentForEspace(params: {
    idEspace?: number;
    databaseCollections?: MyDatabaseCollections;
  }): Promise<RxDocument<PatrimoineDocument> | null> {
    if (!params.databaseCollections) {
      params.databaseCollections = await this.dbService.getDatabaseCollection();
    }
    const patrimoines = await params.databaseCollections.patrimoines.find().exec();
    return (
      patrimoines?.find(pat =>
        (pat.getLatest().toJSON() as unknown as Patrimoine)?.espaces?.some(
          espace => espace.idEspace === params.idEspace
        )
      ) || null
    );
  }

  public async handleComposantIdChange(params: InfosToMutateOfflineDbWithNewComposant): Promise<void> {
    this.removeIdComposantFromStore(params.oldId);
  }

  public removeIdComposantFromStore(idComposant: number, force = false): void {
    if (force || (!isNaN(idComposant) && idComposant < 0)) {
      combineLatest([
        this.store$.select(ComposantSelectors.selectComposantState),
        this.store$.select(ValeurSelectors.selectValeurState)
      ])
        .pipe(
          first(),
          tap(([composantState, valeurState]) => {
            // Suppression des composants & valeurs associées du store (car valeurs pouvant être recréées par le code ailleurs)
            // Donc pour éviter que les id se chevauchent on les supprime directement du store
            if (composantState.entities[idComposant]) {
              const actions = getDefaultDeleteComposantActions(
                composantState.entities[idComposant] as ComposantEntityState
              );
              const valeurs = composantState.entities[idComposant]?.valeurs;
              if (valeurs?.length) {
                actions.push(
                  ...(
                    valeurs.map(valeur =>
                      getDefaultDeleteValeurActions(valeurState.entities[valeur as number] as ValeurEntityState)
                    ) || []
                  ).flat()
                );
              }
              if (actions.length === 1) {
                this.store$.dispatch(actions[0]);
              } else if (actions.length > 1) {
                this.store$.dispatch(getMultiAction(actions, '[Composant] remove one local composant'));
              }
            }
          })
        )
        .subscribe();
    }
  }

  // ====================================================== //
  // ================== SYNCHRONIZATIONS ================== //
  // ====================================================== //
  private async synchronizeRemoveOneComposantDocument(
    composantDocument: RxDocument<ComposantDocument>,
    launchComposantAttenduSynchronisation = true,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    const documentJson = composantDocument.getLatest().toJSON();
    return new Promise((resolve, reject) => {
      this.store$
        .select(ComposantSelectors.selectComposantState)
        .pipe(
          first(),
          switchMap(state =>
            this.composantApiService.deleteComposantWithComposantAttendu(+documentJson.idComposant).pipe(
              map(success => ({ state, success })),
              catchError(async err => {
                if (composantAttendus?.length) {
                  await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
                }
                reject();
                return err;
              })
            )
          ),
          first(),
          tap(async res => {
            if (!res.error && res.success) {
              if (res.state?.entities[+documentJson.idComposant]) {
                this.store$.dispatch(
                  getMultiAction(
                    getDefaultDeleteComposantActions(
                      res.state.entities[+documentJson.idComposant] as ComposantEntityState
                    ),
                    ComposantActions.deleteOneComposantWithComposantAttendu.type
                  )
                );
              }
              const databaseCollections = await this.dbService.getDatabaseCollection();
              await databaseCollections['composants'].bulkRemove([documentJson.uuid]);
              if (composantAttendus?.length) {
                await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
              }
              if (launchComposantAttenduSynchronisation) {
                await this.composantAttenduDbService.synchronizeFromIndexedDb();
              }
              resolve();
            }
          })
        )
        .subscribe();
    });
  }

  private async synchronizeUpdateOneComposantDocument(
    composantDocument: RxDocument<ComposantDocument>,
    launchComposantAttenduSynchronisation = true,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.composantApiService
        .updateOneComposantAndValeurs(composantDocument.getLatest().toMutableJSON() as unknown as Partial<Composant>)
        .pipe(
          first(),
          tap(async res => {
            if (res.composant) {
              const mutableComposant = composantDocument.getLatest().toMutableJSON();
              const valeursObj = transformArrayToObject(mutableComposant?.valeurs, { key: 'idSocieteCaracteristique' });
              const bidirectionalValeurs = res.composant.valeurs?.map(valeur => {
                if (valeursObj[valeur.idSocieteCaracteristique]?.isSynced === false) {
                  if (areValeursEqual(valeursObj[valeur.idSocieteCaracteristique], valeur)) {
                    return valeur;
                  } else {
                    return {
                      ...valeursObj[valeur.idSocieteCaracteristique],
                      idValeur: valeur.idValeur
                    };
                  }
                }
                return valeur;
              });
              const isEspaceUpdated = res.composant.idEspace === mutableComposant.idEspace;
              await composantDocument.incrementalPatch({
                ...res.composant,
                idEspace: isEspaceUpdated ? res.composant.idEspace : mutableComposant.idEspace,
                espace: { idEspace: isEspaceUpdated ? res.composant.idEspace : mutableComposant.idEspace },
                valeurs: bidirectionalValeurs,
                isSynced: isEspaceUpdated && !bidirectionalValeurs.some(valeur => valeur.isSynced === false)
              } as unknown as ComposantDocument);
              if (composantAttendus?.length) {
                await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
              }
              if (launchComposantAttenduSynchronisation) {
                await this.composantAttenduDbService.synchronizeFromIndexedDb();
              }
              resolve();
            }
          }),
          catchError(async err => {
            if (composantAttendus?.length) {
              await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
            }
            reject();
            return of(err);
          })
        )
        .subscribe();
    });
  }

  private async synchronizeCreateOneComposantDocument(
    composantDocument: RxDocument<ComposantDocument>,
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void,
    launchComposantAttenduSynchronisation = true,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    const jsonComposant = composantDocument.getLatest().toJSON() as unknown as Composant;
    return new Promise((resolve, reject) => {
      this.composantApiService
        .createOneComposantAndValeurs(jsonComposant)
        .pipe(
          first(),
          tap(async res => {
            if (res.composant) {
              const oldIdComposant = jsonComposant.idComposant;

              const mutableComposant = composantDocument.getLatest().toMutableJSON();
              const valeursObj = transformArrayToObject(mutableComposant?.valeurs, { key: 'idSocieteCaracteristique' });
              const bidirectionalValeurs = res.composant.valeurs?.map(valeur => {
                if (valeursObj[valeur.idSocieteCaracteristique]?.isSynced === false) {
                  if (areValeursEqual(valeursObj[valeur.idSocieteCaracteristique], valeur)) {
                    return valeur;
                  } else {
                    return {
                      ...valeursObj[valeur.idSocieteCaracteristique],
                      idValeur: valeur.idValeur
                    };
                  }
                }
                return valeur;
              });

              const isEspaceUpdated = res.composant.idEspace === mutableComposant.idEspace;
              await composantDocument.incrementalPatch({
                ...res.composant,
                idEspace: isEspaceUpdated ? res.composant.idEspace : mutableComposant.idEspace,
                espace: { idEspace: isEspaceUpdated ? res.composant.idEspace : mutableComposant.idEspace },
                valeurs: bidirectionalValeurs,
                isSynced: isEspaceUpdated && !bidirectionalValeurs.some(valeur => valeur.isSynced === false)
              } as unknown as ComposantDocument);
              const params: InfosToMutateOfflineDbWithNewComposant = {
                newId: res.composant.idComposant,
                oldId: oldIdComposant,
                idEspace: res.composant.idEspace
              };
              if (callback) {
                callback?.(params);
              } else {
                this.handleComposantIdChange(params);
              }
              if (composantAttendus?.length) {
                await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
              }
              if (launchComposantAttenduSynchronisation) {
                await this.composantAttenduDbService.synchronizeFromIndexedDb();
              }
              resolve();
            }
          }),
          catchError(async err => {
            if (composantAttendus?.length) {
              await this.composantAttenduDbService.updateComposantAttendus(composantAttendus);
            }
            reject();
            return of(err);
          })
        )
        .subscribe();
    });
  }

  private async synchronizeOneComposantDocument(
    composantDocument: RxDocument<ComposantDocument>,
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void,
    launchComposantAttenduSynchronisation = true,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    if (composantDocument?.isInstanceOfRxDocument) {
      const jsonComposant = composantDocument.getLatest().toJSON();
      if (!isNaN(jsonComposant.idComposant)) {
        if (jsonComposant.deleted === true) {
          await this.synchronizeRemoveOneComposantDocument(
            composantDocument,
            launchComposantAttenduSynchronisation,
            composantAttendus
          );
        } else if (jsonComposant.idComposant > 0) {
          await this.synchronizeUpdateOneComposantDocument(
            composantDocument,
            launchComposantAttenduSynchronisation,
            composantAttendus
          );
        } else if (jsonComposant.idComposant < 0) {
          await this.synchronizeCreateOneComposantDocument(
            composantDocument,
            callback,
            launchComposantAttenduSynchronisation,
            composantAttendus
          );
        }
      }
    }
  }

  public async synchronizeIdComposantFromIndexedDb(
    uuid: string,
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocument = await databaseCollections['composants'].findOne({ selector: { uuid } }).exec();
    if (composantDocument) {
      const documentJson = composantDocument.getLatest().toJSON();
      if (documentJson?.isSynced === false) {
        await this.synchronizeOneComposantDocument(composantDocument, callback, true, composantAttendus);
      }
    }
  }

  public async synchronizeFromIndexedDb(
    callback?: (_params: InfosToMutateOfflineDbWithNewComposant) => void,
    composantAttendus?: ComposantAttenduDocument[]
  ): Promise<void> {
    const databaseCollections = await this.dbService.getDatabaseCollection();
    const composantDocuments = await databaseCollections['composants'].find({ selector: { isSynced: false } }).exec();
    const notSyncedComposantDocuments = composantDocuments.filter(
      composant => composant.getLatest().toJSON().isSynced === false
    );
    await Promise.all(
      notSyncedComposantDocuments?.map(composantDocument =>
        this.synchronizeOneComposantDocument(composantDocument, callback, false, composantAttendus)
      )
    );
    await this.composantAttenduDbService.synchronizeFromIndexedDb();
  }

  // ====================================================== //
  // ==================== REFILL STORE ==================== //
  // ====================================================== //
  public handleIndexedDbRefillingStorageComposants<U>(params: {
    databaseCollections: MyDatabaseCollections;
    composantOrFamille: SocieteComposant | SocieteComposantFamille;
    component: U;
    forceSubjects$?: ReplaySubject<void>[];
  }) {
    return combineLatest([
      params.databaseCollections['composants'].find().$.pipe(
        startWith([]),
        map(values => values?.map(el => el.getLatest().toJSON())?.filter(el => !el.deleted)),
        map(values => values.sort((a, b) => compareNumberCustomWithNegatives(+a.idComposant, +b.idComposant))),
        distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr))
      ),
      ...(params.forceSubjects$ || [])
    ]).pipe(
      untilDestroyed(params.component),
      tap(([currentValues]) => {
        if (currentValues?.length) {
          const composantsForStore = filterOnlyUsefulComposants(currentValues, params.composantOrFamille);
          this.store$.dispatch(
            ComposantGeneratedActions.normalizeManyComposantsAfterUpsert({
              composants: composantsForStore.map(el => ({
                ...el,
                idComposant: +el.idComposant
              }))
            })
          );
        }
      })
    );
  }
}
